import type {
  ScreenWakeLockAdapter,
  ScreenWakeLockHandle,
  ScreenWakeLockListener,
  ScreenWakeLockReason,
  ScreenWakeLockSnapshot,
  ScreenWakeLockState,
  WakeLockDocumentTarget,
  WakeLockWindowTarget,
} from "./types.js";

export interface ScreenWakeLockControllerOptions {
  readonly adapter: ScreenWakeLockAdapter;
  readonly document?: WakeLockDocumentTarget;
  readonly window?: WakeLockWindowTarget;
  readonly autoStart?: boolean;
}

export interface ScreenWakeLockController {
  setEnabled(enabled: boolean): Promise<ScreenWakeLockSnapshot>;
  request(reason?: ScreenWakeLockReason): Promise<ScreenWakeLockSnapshot>;
  release(reason?: ScreenWakeLockReason): Promise<ScreenWakeLockSnapshot>;
  getSnapshot(): ScreenWakeLockSnapshot;
  subscribe(listener: ScreenWakeLockListener): () => void;
  dispose(): Promise<void>;
}

export function createScreenWakeLockController(
  options: ScreenWakeLockControllerOptions,
): ScreenWakeLockController {
  const listeners = new Set<ScreenWakeLockListener>();
  let enabled = false;
  let state: ScreenWakeLockState = options.adapter.isSupported() ? "released" : "unsupported";
  let lastReason: ScreenWakeLockReason | undefined;
  let lastError: string | undefined;
  let handle: ScreenWakeLockHandle | null = null;
  let detachRelease: (() => void) | null = null;
  let requestInFlight: Promise<ScreenWakeLockSnapshot> | null = null;
  let disposed = false;

  const snapshot = (): ScreenWakeLockSnapshot => ({
    adapterName: options.adapter.name,
    enabled,
    state,
    lastReason,
    lastError,
  });

  const emit = (): ScreenWakeLockSnapshot => {
    const next = snapshot();
    for (const listener of listeners) listener(next);
    return next;
  };

  const setState = (
    nextState: ScreenWakeLockState,
    reason: ScreenWakeLockReason,
    error?: unknown,
  ): ScreenWakeLockSnapshot => {
    state = nextState;
    lastReason = reason;
    lastError = error ? stringifyError(error) : undefined;
    return emit();
  };

  const releaseCurrent = async (reason: ScreenWakeLockReason): Promise<void> => {
    const current = handle;
    handle = null;
    if (detachRelease) {
      detachRelease();
      detachRelease = null;
    }
    if (current) await current.release();
    if (state !== "unsupported") setState("released", reason);
  };

  const request = async (
    reason: ScreenWakeLockReason = "manual",
  ): Promise<ScreenWakeLockSnapshot> => {
    if (disposed) return snapshot();
    if (!enabled) return snapshot();
    if (!options.adapter.isSupported()) return setState("unsupported", reason);
    if (options.document?.visibilityState === "hidden") {
      await releaseCurrent("visibility");
      return snapshot();
    }
    if (handle && state === "active") return snapshot();
    if (requestInFlight) return requestInFlight;

    requestInFlight = (async () => {
      setState("requesting", reason);
      try {
        const nextHandle = await options.adapter.request();
        if (!enabled || disposed || options.document?.visibilityState === "hidden") {
          await nextHandle.release();
          setState("released", reason);
          return snapshot();
        }
        handle = nextHandle;
        detachRelease =
          nextHandle.onRelease?.(() => {
            handle = null;
            if (detachRelease) {
              detachRelease();
              detachRelease = null;
            }
            setState(enabled ? "released" : "released", "release-event");
            if (enabled && !disposed) void request("release-event");
          }) ?? null;
        return setState("active", reason);
      } catch (error) {
        handle = null;
        return setState("denied", reason, error);
      } finally {
        requestInFlight = null;
      }
    })();

    return requestInFlight;
  };

  const release = async (
    reason: ScreenWakeLockReason = "manual",
  ): Promise<ScreenWakeLockSnapshot> => {
    await releaseCurrent(reason);
    return snapshot();
  };

  const setEnabled = async (nextEnabled: boolean): Promise<ScreenWakeLockSnapshot> => {
    enabled = nextEnabled;
    emit();
    if (!nextEnabled) return release("manual");
    return request("manual");
  };

  const onVisibility = (): void => {
    if (options.document?.visibilityState === "hidden") void release("visibility");
    else if (enabled) void request("visibility");
  };
  const onFocus = (): void => {
    if (enabled) void request("focus");
  };
  const onPageshow = (): void => {
    if (enabled) void request("pageshow");
  };
  const onUserInteraction = (): void => {
    if (enabled && state !== "active") void request("user-interaction");
  };

  options.document?.addEventListener("visibilitychange", onVisibility);
  options.window?.addEventListener("focus", onFocus);
  options.window?.addEventListener("pageshow", onPageshow);
  options.document?.addEventListener("pointerdown", onUserInteraction);
  options.document?.addEventListener("touchstart", onUserInteraction);
  options.document?.addEventListener("keydown", onUserInteraction);

  const dispose = async (): Promise<void> => {
    disposed = true;
    enabled = false;
    options.document?.removeEventListener("visibilitychange", onVisibility);
    options.window?.removeEventListener("focus", onFocus);
    options.window?.removeEventListener("pageshow", onPageshow);
    options.document?.removeEventListener("pointerdown", onUserInteraction);
    options.document?.removeEventListener("touchstart", onUserInteraction);
    options.document?.removeEventListener("keydown", onUserInteraction);
    await release("dispose");
    listeners.clear();
  };

  const controller: ScreenWakeLockController = {
    setEnabled,
    request,
    release,
    getSnapshot: snapshot,
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },
    dispose,
  };

  if (options.autoStart) void setEnabled(true);
  return controller;
}

function stringifyError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}
